import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import TeamLogo from "./TeamLogo";
import { deriveConfidence } from "../lib/pickConfidence";

const fmtML = (n) => {
  if (n == null) return "−170*";
  const v = Number(n);
  return (v > 0 ? "+" : "") + v;
};

/**
 * Given the user's saved ladder + the pick's moneyline, compute the
 * personalized stake. We default to base_unit (tier 1) — the optimizer's
 * tier 2/3 transitions are weekly-P&L-dependent and shown on /optimizer
 * with the full ladder explainer.
 */
const personalStake = (ladder, ml) => {
  if (!ladder || !ladder.base_unit || ml == null) return null;
  const unit = ladder.base_unit;
  const m = Number(ml);
  // Negative ml (favorite) — stake = unit * |ml|/100
  // Positive ml (underdog) — stake = unit flat (potential win > unit)
  // Even (0) — defensive default of unit.
  if (m < 0) return Math.round(unit * Math.abs(m) / 100);
  if (m > 0) return Math.round(unit);
  return Math.round(unit);
};

/**
 * Resolve the moneyline to display for a pick.
 * Priority:
 *   1. `pick.odds.moneyline` — full ESPN payload with provider attribution
 *   2. `pick.moneyline_used` — what the lock service used (may be ESPN or -170 fallback)
 *   3. null — fmtML will render the "−170*" sentinel meaning "no odds available"
 */
const resolveML = (pick) => {
  if (pick?.odds?.moneyline != null) return pick.odds.moneyline;
  if (pick?.moneyline_used != null) return pick.moneyline_used;
  return null;
};

/**
 * Resolve the provider label. Italicizes fallback so it's visually distinct
 * from a real DraftKings line.
 */
const resolveProvider = (pick) => {
  const p = pick?.odds?.provider;
  if (p && !p.toLowerCase().includes("fallback")) return p;
  if (pick?.moneyline_used != null && pick?.odds?.provider) return pick.odds.provider;
  return "ML"; // generic label
};

const fmtGameTimePT = (utcIso) => {
  if (!utcIso) return "";
  try {
    return new Date(utcIso).toLocaleTimeString("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return utcIso; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric",
    });
  } catch { return iso; }
};

// Use America/New_York as the "MLB calendar day" — picks slates flip at midnight ET,
// not at the browser's local timezone.
const etDateOffset = (offsetDays = 0) => {
  const now = new Date();
  // Get the date string in the ET timezone (handles DST automatically)
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const d = parts.find((p) => p.type === "day").value;
  const dateObj = new Date(`${y}-${m}-${d}T12:00:00Z`);
  dateObj.setUTCDate(dateObj.getUTCDate() + offsetDays);
  return dateObj.toISOString().slice(0, 10);
};
const todayISO = () => etDateOffset(0);
const yesterdayISO = () => etDateOffset(-1);
const tomorrowISO = () => etDateOffset(1);

function PickCard({ pick, userLadder }) {
  const accent = pick.analyst === "Charlie" ? "border-[#22C55E]/40" : "border-yellow-400/40";
  const analystColor = pick.analyst === "Charlie" ? "text-[#22C55E]" : "text-yellow-400";

  const status = pick.result || "PENDING";
  const statusBadge = status === "W"
    ? <span className="bg-[#22C55E] text-black font-bold text-xs px-2 py-0.5 rounded-sm">W +${Math.abs(pick.profit || 0).toFixed(0)}</span>
    : status === "L"
      ? <span className="bg-[#FF3B30] text-black font-bold text-xs px-2 py-0.5 rounded-sm">L −${Math.abs(pick.profit || 0).toFixed(0)}</span>
      : status === "SKIP-FILTER" || status === "SKIP-BOTH" || status === "SKIP-WEEK"
        ? <span className="text-[10px] uppercase tracking-wider text-yellow-400 px-1.5 py-0.5 border border-yellow-400/40 rounded-sm">{status}</span>
        : <span className="text-[10px] uppercase tracking-wider text-[#007AFF] px-1.5 py-0.5 border border-[#007AFF]/40 rounded-sm flex items-center gap-1"><Clock size={10} /> Pending</span>;

  const odds = pick.odds;
  const ml = resolveML(pick);
  const provider = resolveProvider(pick);
  const isFallback = (provider || "").toLowerCase().includes("fallback") || (ml == null);
  const locked = pick.is_locked;
  let lockedAtLabel = "";
  if (locked && pick.locked_at) {
    try {
      lockedAtLabel = new Date(pick.locked_at).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      });
    } catch { lockedAtLabel = pick.locked_at; }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-[#121212] border ${accent} rounded-sm p-4 hover:border-white/40 transition-colors`}
      data-testid={`slate-pick-${pick.team_abbr}`}
    >
      <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest">
        <span className={analystColor}>Analyst {pick.analyst}</span>
        <div className="flex items-center gap-1.5">
          {(() => {
            const conf = pick.result ? null : deriveConfidence(pick);
            if (!conf) return null;
            return (
              <span
                title={conf.reason || conf.label}
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold"
                style={{ color: conf.color, border: `1px solid ${conf.color}66`, background: `${conf.color}11` }}
                data-testid={`slate-pick-${pick.team_abbr}-confidence`}
              >
                {conf.label}
              </span>
            );
          })()}
          {locked && (
            <span
              title={lockedAtLabel ? `Locked ${lockedAtLabel}` : "Locked"}
              className="text-[9px] uppercase tracking-wider text-[#22C55E] px-1.5 py-0.5 border border-[#22C55E]/50 rounded-sm font-mono-d"
              data-testid={`slate-pick-${pick.team_abbr}-locked`}
            >
              🔒 LOCKED
            </span>
          )}
          {statusBadge}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-1">
        <TeamLogo sport={pick.sport || "mlb"} abbr={pick.team_abbr} size={40} />
        <div>
          <div className="font-display text-2xl leading-tight">{pick.team}</div>
          <div className="font-mono-d text-xs text-zinc-300 mt-0.5" data-testid={`slate-matchup-${pick.team_abbr}`}>
            {pick.is_home === true ? (
              <><span className="text-zinc-500">{pick.opponent_abbr || pick.opponent} @ </span><span className="text-white font-bold">{pick.team_abbr}</span><span className="ml-1 text-[9px] text-[#22C55E] uppercase tracking-widest">HOME</span></>
            ) : pick.is_home === false ? (
              <><span className="text-white font-bold">{pick.team_abbr}</span><span className="text-zinc-500"> @ {pick.opponent_abbr || pick.opponent}</span><span className="ml-1 text-[9px] text-[#A855F7] uppercase tracking-widest">AWAY</span></>
            ) : (
              <span className="text-zinc-400">vs {pick.opponent}</span>
            )}
          </div>
        </div>
      </div>
      {pick.game_time_utc && (
        <div className="mt-1 text-[10px] text-zinc-500 font-mono-d" data-testid={`slate-time-${pick.team_abbr}`}>
          🕒 {fmtGameTimePT(pick.game_time_utc)} <span className="text-zinc-700">(PT)</span>
        </div>
      )}
      <div className="flex items-end justify-between mt-3 pt-3 border-t border-white/5">
        <div className="text-[10px] text-zinc-500" data-testid={`slate-stake-${pick.team_abbr}`}>
          {(() => {
            const yours = personalStake(userLadder, ml);
            if (yours) {
              return (
                <div>
                  <div className="text-[#22C55E] uppercase tracking-widest font-bold text-[9px]">Your stake</div>
                  <div className="text-zinc-200 font-mono-d text-base">${yours}</div>
                  {pick.stake ? (
                    <div className="text-[9px] text-zinc-600 line-through font-mono-d">${pick.stake.toFixed(0)} default</div>
                  ) : null}
                </div>
              );
            }
            if (pick.stake) {
              return (
                <div>
                  <span className="uppercase tracking-widest text-[9px] mr-1">Stake</span>
                  <span className="text-zinc-300 font-mono-d">${pick.stake.toFixed(0)}</span>
                  {/* Hint anonymous + non-personalized users towards /optimizer */}
                  <Link to="/optimizer" className="block text-[9px] mt-0.5 text-[#22D3EE] hover:text-white uppercase tracking-widest" data-testid={`slate-optimizer-link-${pick.team_abbr}`}>
                    Personalize →
                  </Link>
                </div>
              );
            }
            return "—";
          })()}
        </div>
        <div className="text-right">
          <div
            className={`text-[10px] uppercase tracking-widest ${isFallback ? "text-zinc-600 italic" : "text-zinc-500"}`}
            title={isFallback ? "ESPN didn't have live odds for this game — showing -170 estimate" : `Live odds from ${provider}`}
          >
            {isFallback ? "Est." : (provider === "ML" ? "ML" : provider)}
          </div>
          <div className={`font-display text-xl ${isFallback ? "text-zinc-400" : "text-white"}`}>{fmtML(ml)}</div>
        </div>
      </div>
    </motion.div>
  );
}

function SlateSection({ label, dateISO, picks, kind, emptyMessage, userLadder }) {
  return (
    <div className="mb-8" data-testid={`slate-${kind}`}>
      <div className="flex items-baseline gap-3 mb-4 pb-2 border-b border-white/10">
        <Calendar size={20} weight="fill" className="text-[#007AFF]" />
        <h2 className="font-display text-3xl tracking-wider">{label}</h2>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono-d">{fmtDate(dateISO)}</div>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
          {picks.length} {picks.length === 1 ? "pick" : "picks"}
        </div>
      </div>
      {picks.length === 0 ? (
        <div className="bg-[#121212] border border-dashed border-white/10 rounded-sm p-6 text-center" data-testid={`slate-${kind}-empty`}>
          <div className="text-sm text-zinc-400 mb-1">{emptyMessage}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {picks.map((p, i) => <PickCard key={i} pick={p} userLadder={userLadder} />)}
        </div>
      )}
    </div>
  );
}

/**
 * The headline "Daily Slate" panel shown on the admin landing.
 * Replaces the previous AdminTeaser/UpcomingPicks combo. Three sections:
 *  - "Today's Slate" — every pick whose target_date == today (settled OR pending)
 *  - "Tomorrow's Slate" — every pending pick whose target_date == tomorrow
 *  - "Day After" (only if non-empty) — picks queued further out
 */
export default function DailySlate({ settledPicks, upcomingPicks, userLadder }) {
  const today = todayISO();
  const yesterday = yesterdayISO();
  const tomorrow = tomorrowISO();

  const { yesterdays, todays, tomorrows, dayAfter, yesterdayHadGames } = useMemo(() => {
    const yesterdayResults = settledPicks.filter((p) => p.date === yesterday);

    const settledToday = settledPicks.filter((p) => p.date === today);
    const pendingToday = upcomingPicks.filter((p) => p.target_date === today);
    const todaysAll = [...settledToday, ...pendingToday];

    const tomorrowsAll = upcomingPicks.filter((p) => p.target_date === tomorrow);

    const byDate = {};
    upcomingPicks
      .filter((p) => p.target_date && p.target_date > tomorrow)
      .forEach((p) => { (byDate[p.target_date] = byDate[p.target_date] || []).push(p); });

    // Heuristic: if at least one settled pick exists for yesterday, we know
    // yesterday's slate has been processed.
    return {
      yesterdays: yesterdayResults,
      todays: todaysAll,
      tomorrows: tomorrowsAll,
      dayAfter: byDate,
      yesterdayHadGames: yesterdayResults.length > 0,
    };
  }, [settledPicks, upcomingPicks, today, yesterday, tomorrow]);

  // Smart messages: distinguish "no triggers fired" vs "slate not yet generated".
  // If yesterday produced at least one settled pick OR any settled picks exist with
  // date >= yesterday, the slate has been processed — empty TODAY means 0 triggers.
  const todayMessage = yesterdayHadGames
    ? `No picks for today (${fmtDate(today)}). None of the tracked teams ended yesterday in a qualifying state (iso-1L or W1-after-L). Open Strategy → Trigger Map for the full audit.`
    : `No picks for today. Slate will be set after tonight's MLB games close (last results land ~1:00 AM ET).`;

  const tomorrowMessage = `No picks queued for tomorrow yet. Tomorrow's slate is generated after tonight's MLB games settle — picks lock around 1:00 AM ET.`;

  return (
    <div className="mb-12" data-testid="daily-slate">
      <SlateSection
        label="TODAY'S PREDICTIONS"
        dateISO={today}
        picks={todays}
        kind="today"
        emptyMessage={todayMessage}
        userLadder={userLadder}
      />
      <SlateSection
        label="TOMORROW'S PREDICTIONS"
        dateISO={tomorrow}
        picks={tomorrows.map((p) => ({
          ...p,
          team: p.team,
          opponent: p.opponent || (p.target_gamePk ? "TBD" : "—"),
          analyst: p.bet_type === "wc" ? "Dakota" : "Charlie",
          result: null,
        }))}
        kind="tomorrow"
        emptyMessage={tomorrowMessage}
        userLadder={userLadder}
      />
      {yesterdays.length > 0 && (
        <SlateSection
          label="YESTERDAY'S RESULTS"
          dateISO={yesterday}
          picks={yesterdays}
          kind="yesterday"
          emptyMessage=""
          userLadder={userLadder}
        />
      )}
      {Object.keys(dayAfter).sort().map((dt) => (
        <SlateSection
          key={dt}
          label="LATER THIS WEEK"
          dateISO={dt}
          picks={dayAfter[dt].map((p) => ({
            ...p,
            analyst: p.bet_type === "wc" ? "Dakota" : "Charlie",
            opponent: p.opponent || "TBD",
          }))}
          kind="later"
          emptyMessage=""
          userLadder={userLadder}
        />
      ))}
    </div>
  );
}
